import {Modal} from 'antd';
import {useEffect, useRef} from 'react';

import {Spinner} from '../../../layout/spinner/spinner';
import {Locale} from '../../../provider/locale/locale';
import {useModal} from '../../../provider/modal/modal-hook';
import {useEvent, useUpdateEvent} from '../../../service/events/events-hook';
import {CommonEventStatusEnum, EventFormType} from '../../../service/events/events-type';
import {addCompaniesApi} from '../../../service/feature-companies/feature-companies-api';
import {postCompanySelectorCompanySchema} from '../../../service/feature-companies/feature-companies-api-type';
import {CompaniesSelectorFeatureEnum} from '../../../service/feature-companies/feature-companies-const';
import {useCompaniesSelector} from '../../../service/feature-companies/feature-companies-hook';
import {PostSelectorCompanyType} from '../../../service/feature-companies/feature-companies-type';
import {noop} from '../../../util/function';

import {EventForm} from './event-form';
import {EVENT_FORM_MODAL_WIDTH, getEventFormInitialValues, prepareEventRequestData} from './event-form-helper';

type PropsType = {
    eventId: number;
    setIsOpen: (isOpen: boolean) => void;
    onUpdateSuccess?: VoidFunction;
};

export function EditEventFormModal({eventId, setIsOpen, onUpdateSuccess}: PropsType): JSX.Element {
    const {data: event, isFetching} = useEvent(eventId, true);
    const {mutateAsync: updateEvent} = useUpdateEvent(eventId);
    const {modal} = useModal();

    const companiesSelector = useCompaniesSelector<PostSelectorCompanyType>({
        feature: CompaniesSelectorFeatureEnum.Posts,
        companySchema: postCompanySelectorCompanySchema,
        resourceId: null,
        initialCompaniesCount: event?.companiesIds.length ?? 0,
    });

    const {initializeSelector, selectorId, setCompaniesSelectorFilter} = companiesSelector;

    useEffect(() => {
        initializeSelector();
    }, [initializeSelector]);

    // Seed the fresh posts selector with the event's existing companies, then reload to reflect them.
    const seededRef = useRef<boolean>(false);

    useEffect(() => {
        if (!event || !selectorId || seededRef.current) {
            return;
        }

        seededRef.current = true;

        if (event.companiesIds.length > 0) {
            addCompaniesApi({
                selectorId,
                body: {ids: event.companiesIds},
                feature: CompaniesSelectorFeatureEnum.Posts,
            })
                .then(() => setCompaniesSelectorFilter((previousFilter) => ({...previousFilter})))
                .catch(noop);
        }
    }, [event, selectorId, setCompaniesSelectorFilter]);

    async function handleSubmit(values: EventFormType, status: CommonEventStatusEnum): Promise<void> {
        await updateEvent(prepareEventRequestData(values, selectorId, status));
        onUpdateSuccess?.();
    }

    function handleCancel(): void {
        modal.confirm({
            maskClosable: true,
            title: <Locale stringKey="EVENTS__EDIT__CONFIRM_CLOSE__TITLE" />,
            content: <Locale stringKey="EVENTS__CONFIRM_CLOSE__CONTENT" />,
            okText: <Locale stringKey="EVENTS__EDIT__CONFIRM_CLOSE__CONFIRM" />,
            cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
            okButtonProps: {danger: true, type: 'primary'},
            onOk: () => setIsOpen(false),
        });
    }

    return (
        <Modal
            centered
            destroyOnClose
            footer={null}
            onCancel={handleCancel}
            open
            title={<Locale stringKey="EVENTS__EDIT__TITLE" />}
            width={EVENT_FORM_MODAL_WIDTH}
        >
            {isFetching || !event ? (
                <Spinner />
            ) : (
                <EventForm
                    closeModal={() => setIsOpen(false)}
                    companiesSelector={companiesSelector}
                    initialValues={getEventFormInitialValues(event)}
                    mode="edit"
                    onCancel={handleCancel}
                    onSubmit={handleSubmit}
                />
            )}
        </Modal>
    );
}
